// Payment and accounting instants are shown on the firm's calendar day.
// A charge at 7:03 PM Eastern is 00:03 UTC the next morning; slicing the
// ISO string to 10 characters shows that UTC day. Date-only values and
// midnight-UTC serializations of DATE columns stay on the stored civil day
// (those are not instants). Defaults to America/New_York when the firm
// payload has no timezone.

export const DEFAULT_FIRM_TIME_ZONE = 'America/New_York';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const TZ_KEYS = ['timezone', 'time_zone', 'tz', 'firm_timezone'];

let activeTimeZone = DEFAULT_FIRM_TIME_ZONE;

export function isValidTimeZone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch (_) {
    return false;
  }
}

export function firmTimeZoneFrom(source) {
  if (!source || typeof source !== 'object') return DEFAULT_FIRM_TIME_ZONE;
  for (let i = 0; i < TZ_KEYS.length; i++) {
    const raw = source[TZ_KEYS[i]];
    if (typeof raw === 'string' && isValidTimeZone(raw.trim())) return raw.trim();
  }
  return DEFAULT_FIRM_TIME_ZONE;
}

export function setActiveFirmTimeZone(tz) {
  activeTimeZone = isValidTimeZone(tz) ? tz : DEFAULT_FIRM_TIME_ZONE;
}

export function activeFirmTimeZone() {
  return activeTimeZone;
}

// YYYY-MM-DD, or a DATE column sent as midnight UTC. A real instant such as
// 2026-11-02T00:03:00.000Z is not midnight and is not a civil date.
export function isCivilDate(value) {
  const s = String(value == null ? '' : value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return true;
  return /^\d{4}-\d{2}-\d{2}[T ]00:00:00(\.0+)?(Z|[+-]00:?00)?$/.test(s);
}

export function ymdInZone(date, timeZone = activeTimeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type) => {
    const part = parts.find((p) => p.type === type);
    return part ? part.value : '';
  };
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function firmDateYmd(value, timeZone = activeTimeZone) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (isCivilDate(s)) return s.slice(0, 10);
  const ms = Date.parse(s);
  if (Number.isNaN(ms)) {
    const head = s.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(head) ? head : '';
  }
  return ymdInZone(new Date(ms), timeZone);
}

export function formatFirmDate(value, timeZone = activeTimeZone) {
  if (value == null || value === '') return '—';
  const ymd = firmDateYmd(value, timeZone);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return String(value);
  const parts = ymd.split('-').map(Number);
  const month = MONTHS[parts[1] - 1];
  if (!month || !parts[2]) return '—';
  return `${month} ${parts[2]}, ${parts[0]}`;
}

export function firmTodayYmd(timeZone = activeTimeZone, now = new Date()) {
  return ymdInZone(now, timeZone);
}

export function addCalendarDays(ymd, days) {
  const parts = String(ymd).split('-').map(Number);
  const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

// This year and Last 30 days are firm-local calendar days, not UTC and not
// the browser's zone.
export function firmRangeFor(preset, timeZone = activeTimeZone, now = new Date()) {
  if (!preset || preset === 'all') return { from: '', to: '' };
  const today = firmTodayYmd(timeZone, now);
  const year = today.slice(0, 4);
  if (preset === 'this-year') return { from: `${year}-01-01`, to: `${year}-12-31` };
  if (preset === '30') return { from: addCalendarDays(today, -30), to: today };
  return { from: '', to: '' };
}
